/* =====================================================================
   pdp-personalizer.js — Option B PDP behaviors
   ---------------------------------------------------------------------
   Loaded ONLY on eligible personalized PDPs (engraving + photo singles).
   The ab_pdp_v1 A/B test concluded 2026-08-21 with B as the winner; the
   inline script in product-template.liquid now sets html.pdp-b for every
   visitor (before first paint, no flicker) and exposes:

       window.__pdpAB = { name, variant:'B', active:false, cartAttr }

   This file does the non-critical-path work:
     1. Test-era tracking (ab_pdp_test_v1 cart attr + GA4 assignment
        event) — kept behind AB.active, which is now permanently false;
        inert, retained so a future test can re-arm the same plumbing.
     2. Sticky bottom Add-to-Cart proxy on mobile that forwards clicks
        to the real ATC button (so all personalizer property-harvesting
        / confirm-modals still run).
   ===================================================================== */
(function () {
  'use strict';

  var AB = window.__pdpAB;
  if (!AB || AB.variant !== 'B') return;         // not an eligible Option B PDP -> no-op
  var VARIANT = AB.variant === 'B' ? 'B' : 'A';
  var CART_ATTR = AB.cartAttr || 'ab_pdp_test_v1';
  var GA4_TEST_NAME = 'pdp_option_b_v1';

  /* ---------------------------------------------------------------
     1. Cart-attribute stamping (exposure tracking)
     --------------------------------------------------------------- */
  function stampCart() {
    var attrs = {};
    attrs[CART_ATTR] = VARIANT;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  function trackAssignment() {
    stampCart().catch(function () { /* retried on add-to-cart */ });

    // Re-stamp on every add-to-cart (cart guaranteed to exist by then).
    if (typeof jQuery !== 'undefined') {
      try {
        jQuery(document.body).on('afterAddItem.ajaxCart added.ajaxProduct', function () {
          stampCart().catch(function () {});
        });
      } catch (e) {}
    }
    // Belt-and-suspenders: also re-stamp when the real ATC is submitted.
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest && e.target.closest('.add-to-cart-btn');
      if (t) setTimeout(function () { stampCart().catch(function () {}); }, 400);
    }, true);

    // GA4 cross-check event. MUST use the ab_test_assignment schema — it is
    // the only A/B event GTM-5TT8DJX forwards (trigger CE - ab_test_assignment,
    // params ab_test_name/ab_test_variant registered as GA4 custom dimensions).
    // The theme never defines window.gtag; dataLayer is the working path.
    // (Primary tracking is the first-party beacon + cart attribute — this is
    // segmentation backup only.)
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'ab_test_assignment',
        ab_test_name: GA4_TEST_NAME,
        ab_test_variant: VARIANT
      });
    } catch (e) {}
  }

  /* ---------------------------------------------------------------
     2. Sticky Add-to-Cart proxy (variant B, mobile)
     The proxy forwards clicks to the REAL button so the theme's
     personalizer harvesting / confirm modals all still fire.
     --------------------------------------------------------------- */
  function buildStickyAtc() {
    if (VARIANT !== 'B') return;
    if (document.querySelector('.wf-sticky-atc')) return;

    var realBtn = document.querySelector('.product-single__form .add-to-cart-btn')
               || document.querySelector('.add-to-cart-btn');
    if (!realBtn) return;

    // The locket ships its own sticky proxy (.lke-sticky-atc); never double up.
    if (document.querySelector('.lke-sticky-atc')) return;

    // current price only — .product__price also matches the struck compare price
    var priceEl = document.querySelector('.product-single__meta .product__price:not(.product__price--compare)');

    var bar = document.createElement('div');
    bar.className = 'wf-sticky-atc';
    var proxy = document.createElement('button');
    proxy.type = 'button';
    proxy.className = 'wf-sticky-proxy';
    bar.appendChild(proxy);
    document.body.appendChild(bar);
    document.body.classList.add('wf-has-sticky');

    function syncLabel() {
      var label = (realBtn.textContent || 'Add to cart').trim() || 'Add to cart';
      var price = priceEl ? (priceEl.textContent || '').trim() : '';
      proxy.innerHTML = '';
      proxy.appendChild(document.createTextNode(label));
      if (price) {
        var span = document.createElement('span');
        span.className = 'p';
        span.textContent = '· ' + price;
        proxy.appendChild(span);
      }
      proxy.disabled = !!realBtn.disabled;
    }
    syncLabel();

    // keep proxy price/disabled in sync as the theme updates the real button
    try {
      var mo = new MutationObserver(syncLabel);
      mo.observe(realBtn, { attributes: true, childList: true, subtree: true, characterData: true });
      if (priceEl) mo.observe(priceEl, { childList: true, subtree: true, characterData: true });
    } catch (e) {}

    proxy.addEventListener('click', function () {
      if (realBtn.disabled) return;
      realBtn.click();
      realBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    /* personalization gate: the sticky must not invite an add-to-cart before
       the buyer has personalized — premature sticky taps produce
       un-personalized orders (support tickets, remakes). Gates only on what
       the page actually has: at least one engraving line when text entry
       exists (inputs or symbol-dropdown components), and a real uploaded
       photo when the uploader exists. Fonts always have a default selected,
       so they never block. The main ATC button is untouched. */
    function personalized() {
      /* handwriting-engraving mode (snippets/handwriting-engraving.liquid):
         the typed-text inputs are hidden, so gate on the uploaded photo */
      var handForm = document.querySelector('.product-single__form.wf-hand-on');
      if (handForm) {
        var handUrl = handForm.querySelector('.wf-hand-url');
        return !!(handUrl && handUrl.value);
      }
      /* Custom Keepsake Flag (snippets/flag-picker.liquid): gate on the chosen
         flag, whose hidden input stays disabled until one is picked */
      var flagIn = document.querySelector('.product-single__form [data-wf-flag-field] input[name="properties[Flag]"]');
      if (flagIn && (flagIn.disabled || !flagIn.value)) return false;
      /* Painted Pet Portrait (snippets/painted-background-picker.liquid):
         gate on the chosen background (no default) */
      var pbg = document.querySelector('.product-single__form [data-wf-pbg-field]');
      if (pbg && !pbg.querySelector('input.wf-pbg__input:checked')) return false;
      var texts = [].filter.call(document.querySelectorAll(
        '.product-single__form .symbol-input,' +
        '.product-single__form .custom-ring-field.input input[type="text"],' +
        '.product-single__form .custom-ornament-field.input input[type="text"],' +
        '.product-single__form #input-text'
      ), function (i) { return i.offsetParent; });
      var comps = document.querySelectorAll('.symbol-dropdown-parent .active-character');
      if (texts.length || comps.length) {
        var hasText = texts.some(function (i) { return (i.value || '').trim(); })
          || [].some.call(comps, function (c) { return (c.textContent || '').trim(); });
        if (!hasText) return false;
      } else if (document.getElementById('ornament-type') || document.getElementById('custom-ring-type')
          || document.getElementById('FONT') || document.querySelector('.product-single__form .wf-customring-block')) {
        // engravable page whose inputs the theme JS hasn't mounted yet — these
        // markers are server-rendered, so hold the sticky instead of letting it
        // flash on load and vanish once the empty inputs appear
        return false;
      }
      /* Photo Quantity 2+ (snippets/photo-quantity-upload.liquid): the single
         uploader is parked, so gate on every active photo slot instead */
      var pq = document.querySelector('.product-single__form [data-wf-pq].is-on');
      if (pq) return pq.getAttribute('data-wf-pq-complete') === '1';
      var up = document.getElementById('upload-preview');
      if (up && photoUploadEl()) {
        var s = up.getAttribute('src') || '';
        if (!s || s.indexOf('data:image/svg') > -1) return false;
      }
      return true;
    }
    // show the sticky bar only while the real button is off-screen AND the page is personalized
    var offscreen = false;
    function sync() { bar.classList.toggle('is-show', offscreen && personalized()); }
    function evalVisibility() {
      var r = realBtn.getBoundingClientRect();
      offscreen = !(r.top < (window.innerHeight - 40) && r.bottom > 0);
      sync();
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        offscreen = !entries[0].isIntersecting;
        sync();
      }, { rootMargin: '0px 0px -40px 0px' });
      io.observe(realBtn);
    } else {
      window.addEventListener('scroll', evalVisibility, { passive: true });
      window.addEventListener('resize', evalVisibility);
      evalVisibility();
    }
    var sdeb = null;
    function onEdit() { if (sdeb) clearTimeout(sdeb); sdeb = setTimeout(sync, 80); }
    document.addEventListener('input', onEdit, true);
    document.addEventListener('change', onEdit, true);
    setInterval(sync, 800);   // photo uploads + dropdown picks don't fire input events
  }

  /* ---------------------------------------------------------------
     boot
     --------------------------------------------------------------- */
  /* ---------------------------------------------------------------
     3. Move the photo uploader to the bottom (just above the live-preview
     card), so the order is: options -> engraving -> photo -> preview -> ATC.
     --------------------------------------------------------------- */
  function photoUploadEl() {
    var ups = [].slice.call(document.querySelectorAll('.product-single__form .upload'));
    for (var i = 0; i < ups.length; i++) { if (ups[i].querySelector('#upload-file, .btn__file-picker')) return ups[i]; }
    return null;
  }
  function atcAnchor() {
    var atc = document.querySelector('.product-single__form .add-to-cart-btn');
    if (!atc) return null;
    return atc.closest('.payment-buttons') || atc;
  }
  function moveUploadToBottom() {
    if (VARIANT !== 'B') return;
    var up = photoUploadEl(), anchor = atcAnchor();
    if (!up || !anchor || !anchor.parentNode) return;
    // Sit the uploader just ABOVE the live-preview card (which itself sits just
    // above the ATC). Until that card exists, sit it just above the ATC.
    var card = document.querySelector('.wf-livecard');
    var target = (card && card.parentNode === anchor.parentNode) ? card : anchor;
    if (up.nextElementSibling === target) return;           // already in place
    target.parentNode.insertBefore(up, target);
  }

  /* ---------------------------------------------------------------
     2. Inline live-preview card. Relocate the theme's self-contained
     engraving overlay (product image + live canvases/text) into a gold
     card placed below the photo uploader (just above the ATC) — reusing the
     theme's exact per-product rendering, so the preview is shaped per product.
     --------------------------------------------------------------- */
  function productPhotoSrc() {
    var cands = [].slice.call(document.querySelectorAll('.product__main-photos img, .product__photos img'));
    for (var i = 0; i < cands.length; i++) {
      var s = cands[i].currentSrc || cands[i].src || '';
      if (s && s.indexOf('/cdn/shop/') > -1 && cands[i].naturalWidth > 50) return s;
    }
    var og = (document.querySelector('meta[property="og:image"]') || {}).content || '';
    return og.replace(/^http:/, 'https:');
  }

  /* ---- 2a. SVG-shape preview (Claude-Design look): a stylized vector of the
     product, recolored to the selected finish, with the live engraving drawn on
     it. Uses window.WF_PREVIEW_SHAPES (assets/pdp-preview-shapes.js). ---- */
  var WF_METALS = {
    silver: ['#fbfbfd', '#c9ccd2', '#8b9098'],
    gold:   ['#f6e7bd', '#d7ab63', '#9c7330'],
    rose:   ['#f7e3da', '#e3a589', '#b06b50']
  };
  function selectedMetalKey() {
    var v = '';
    var sw = document.querySelector('.product-single__form .variant-input input.color-swatch-input:checked, .product-single__form .variant-input input[type="radio"]:checked');
    if (sw) v = sw.getAttribute('data-color-name') || sw.value || '';
    if (!v) { var info = document.querySelector('[id^="VariantColorLabel"]'); if (info) v = info.textContent || ''; }
    v = (v || '').toLowerCase();
    if (v.indexOf('rose') > -1) return 'rose';
    if (v.indexOf('gold') > -1 || v.indexOf('brass') > -1) return 'gold';
    return 'silver';
  }
  function engravingLines() {
    var lines = [].slice.call(document.querySelectorAll('.pdp-overlay-container .preview-text'))
      .map(function (s) { return (s.textContent || '').trim(); }).filter(Boolean);
    if (lines.length) return lines;
    ['TEXT1', 'TEXT2', 'TEXT3', 'TEXT4'].forEach(function (id) { var el = document.getElementById(id); if (el && el.value) lines.push(el.value.trim()); });
    if (!lines.length) { var si = document.querySelector('.symbol-input'); if (si && si.value) lines.push(si.value.trim()); }
    if (!lines.length) { var it = document.getElementById('input-text'); if (it && it.value) lines.push(it.value.trim()); }
    return lines.filter(Boolean);
  }
  function engravingFont() {
    var b = document.querySelector('.font-button.active');
    if (b && b.getAttribute('data-font')) return b.getAttribute('data-font');
    return 'Georgia';
  }
  function currentVariantId() {
    var sel = document.querySelector('.product-single__form [name="id"]');
    if (sel && sel.value) return String(sel.value);
    var m = (location.search.match(/[?&]variant=(\d+)/) || [])[1];
    return m ? String(m) : null;
  }
  /* Draw the live engraving into svg's .wf-engrave-layer, scaled to FILL the
     text-area box (ta.w x ta.h) as large as it fits. We render at a reference
     size, measure the REAL rendered bounds with getBBox() — so each font's own
     metrics decide the fit (scripts, wide caps, etc. all auto-size correctly) —
     then uniformly scale to the box. ta.fontScale (<=1) is an optional inset so
     the text can stop short of the box edges. */
  var WF_FONTS_TRIED = {};
  var WF_INK_CANVAS = null;
  function inkCtx() {
    if (!WF_INK_CANVAS) WF_INK_CANVAS = document.createElement('canvas');
    return WF_INK_CANVAS.getContext('2d');
  }
  function fontStack(fontName) { return '"' + fontName + '", Icons, Georgia, serif'; }
  // Icons symbol glyphs live in the PUA block (angel U+E90C, heart, ...).
  // Codepoint comparisons, not regex literals, so the source stays ASCII.
  function isSymCp(cp) { return cp >= 0xE000 && cp <= 0xF8FF; }
  function hasSym(s) {
    for (var i = 0; i < s.length; i++) { if (isSymCp(s.charCodeAt(i))) return true; }
    return false;
  }
  /* split a line into runs of plain text vs Icons symbol glyphs */
  function symRuns(t) {
    var runs = [], cur = null;
    for (var i = 0; i < t.length; i++) {
      var sym = isSymCp(t.charCodeAt(i));
      if (!cur || cur.sym !== sym) { cur = { s: '', sym: sym }; runs.push(cur); }
      cur.s += t.charAt(i);
    }
    return runs;
  }
  /* symbols render EVEN with the letters: the Icons glyphs fill their em (a
     heart towers over caps), while real engraving cuts symbols at letter
     height — scale each symbol run so its ink height matches the current
     font's cap height. */
  function symRunScale(fontName, REF, runText) {
    try {
      var c = inkCtx(); if (!c) return 1;
      c.font = REF + 'px ' + fontStack(fontName);
      var capA = c.measureText('HA').actualBoundingBoxAscent;
      var symA = c.measureText(runText).actualBoundingBoxAscent;
      if (!capA || !symA) return 1;
      return Math.max(0.4, Math.min(1, capA / symA));
    } catch (e) { return 1; }
  }
  /* symbols also center OPTICALLY on the letters they sit beside. The anchor
     is the band from the baseline to the letters' MEAN top (each letter's own
     ink ascent, descenders ignored so "nugget" isn't dragged low): all caps ->
     cap band, all x-height ("emma") -> x-height band, mixed ("Bella") -> in
     between. Like a letter without a descender, a symbol is never pushed below
     the baseline — the factory engraves symbols on the text baseline — so a
     cap-height paw beside "Bella" stays on it instead of sinking under it,
     while a glyph that floats above the baseline (infinity beside "hey") can
     still settle down onto it. Returns a baseline dy in REF units (+ = down,
     0 = no shift, e.g. symbol-only lines). */
  function symRunShift(fontName, REF, plainText, runText, symSize) {
    try {
      if (!plainText) return 0;
      var c = inkCtx(); if (!c) return 0;
      var stack = fontStack(fontName);
      c.font = REF + 'px ' + stack;
      var sumA = 0, n = 0;
      for (var i = 0; i < plainText.length; i++) {
        var ch = plainText.charAt(i);
        if (!/[0-9A-Za-zÀ-ɏ]/.test(ch)) continue;   // spaces / punctuation carry no band
        var a = c.measureText(ch).actualBoundingBoxAscent;
        if (typeof a !== 'number') return 0;
        sumA += a; n++;
      }
      if (!n) return 0;
      c.font = symSize + 'px ' + stack;
      var mS = c.measureText(runText);
      var aS = mS.actualBoundingBoxAscent, dS = mS.actualBoundingBoxDescent;
      if (typeof aS !== 'number' || typeof dS !== 'number') return 0;
      var dy = (-(sumA / n) / 2) - ((dS - aS) / 2);   // band centre minus symbol ink centre
      return Math.min(dy, Math.max(0, -dS));           // never push the symbol's ink below the baseline
    } catch (e) { return 0; }
  }
  /* True INK bounds of the engraving as it is laid out in the SVG (each line's
     tspan anchored middle at x=0, baselines stacked by `lh`, symbol runs at
     their cap-height-matched size), measured per-font with the canvas
     TextMetrics actualBoundingBox* properties. This is the font's real visible
     extent — unlike SVG getBBox(), which returns the em-box and makes script
     faces size and centre inconsistently. "Icons" is explicitly in the stack,
     matching the SVG, so PUA symbol glyphs resolve identically in both.
     Returns {x,y,width,height} in REF units, or null when the metrics aren't
     available (older browsers). */
  function inkBounds(lines, fontName, REF, lh) {
    try {
      var c = inkCtx();
      if (!c) return null;
      var stack = fontStack(fontName);
      c.textAlign = 'left'; c.textBaseline = 'alphabetic';
      var x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, any = false;
      for (var i = 0; i < lines.length; i++) {
        var t = lines[i]; if (!t) continue;
        // measure per run (matching the SVG's per-run font sizes), compose by
        // advance; no kerning across run boundaries in SVG tspans either
        var runs = symRuns(t), parts = [], xAdv = 0, j;
        var plain = runs.filter(function (r) { return !r.sym; }).map(function (r) { return r.s; }).join('');
        for (j = 0; j < runs.length; j++) {
          var size = runs[j].sym ? REF * symRunScale(fontName, REF, runs[j].s) : REF;
          c.font = size + 'px ' + stack;
          var m = c.measureText(runs[j].s);
          var aA = m.actualBoundingBoxAscent, aD = m.actualBoundingBoxDescent;
          if (typeof aA !== 'number' || typeof aD !== 'number') return null;   // ink vertical unsupported
          // sym runs baseline-shift to the letters' ink centre in the SVG (symRunShift) — mirror it here
          var sh = runs[j].sym ? symRunShift(fontName, REF, plain, runs[j].s, size) : 0;
          parts.push({ x: xAdv, w: m.width || 0, aL: m.actualBoundingBoxLeft, aR: m.actualBoundingBoxRight, aA: aA, aD: aD, sh: sh });
          xAdv += m.width || 0;
        }
        var by = i * lh;                                  // this line's baseline (matches its SVG tspan y)
        for (j = 0; j < parts.length; j++) {
          var pr = parts[j], lx0, lx1;
          if (typeof pr.aL === 'number' && typeof pr.aR === 'number' && (pr.aL + pr.aR) > 0.01) {
            lx0 = pr.x - pr.aL; lx1 = pr.x + pr.aR;      // ink-tight horizontal (incl. side bearings / swashes)
          } else { lx0 = pr.x; lx1 = pr.x + pr.w; }      // fallback: advance box
          lx0 -= xAdv / 2; lx1 -= xAdv / 2;              // the SVG line is advance-centred (text-anchor middle)
          if (lx0 < x0) x0 = lx0; if (lx1 > x1) x1 = lx1;
          if (by + pr.sh - pr.aA < y0) y0 = by + pr.sh - pr.aA;
          if (by + pr.sh + pr.aD > y1) y1 = by + pr.sh + pr.aD;
          any = true;
        }
      }
      if (!any || !isFinite(x0) || !isFinite(y0) || x1 <= x0 || y1 <= y0) return null;
      return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    } catch (e) { return null; }
  }
  /* positional line values for per-line spots (textAreas array): index i must
     stay line i even when earlier lines are empty, so empties are KEPT (unlike
     engravingLines, which collapses them). The symbol-dropdown components are
     the line-ordered source of truth (their DOM order matches the Line 1..N
     labels); the legacy #ornament-textN-overlay mirror spans are NOT
     line-ordered — the old overlay numbers them by its own photo layout (on
     the quadrant necklace, Line 1 lands in span 4) — so spans/#TEXTn are only
     the fallback when no components exist. Returns null when every line is
     empty. */
  function positionalLines(n) {
    var comps = document.querySelectorAll('.symbol-dropdown-parent .active-character');
    var sins = document.querySelectorAll('.symbol-input');   // multi-row symbol-input products (circle photo necklace)
    var spans = document.querySelectorAll('.pdp-overlay-container .preview-text');
    var out = [], any = false;
    for (var i = 0; i < n; i++) {
      var v = '';
      if (comps.length) {
        v = comps[i] ? (comps[i].textContent || '').trim() : '';
      } else if (sins.length) {
        v = sins[i] ? (sins[i].value || '').trim() : '';
      } else {
        var el = document.getElementById('TEXT' + (i + 1));
        if (el && el.value) v = el.value.trim();
        if (!v && spans[i]) v = (spans[i].textContent || '').trim();
      }
      out.push(v); if (v) any = true;
    }
    return any ? out : null;
  }
  function drawEngrave(svg, ta, linesOverride) {
    var layer = svg.querySelector('.wf-engrave-layer'); if (!layer) return;
    var fontName = engravingFont().replace(/"/g, '');
    if (ta && typeof ta.length === 'number') {           // textAreas array: one spot per line
      var pl = positionalLines(ta.length);
      layer.textContent = '';
      if (!pl) return;
      ensureEngraveFonts(pl.join(''), fontName, function () { drawEngrave(svg, ta); });
      for (var si = 0; si < ta.length; si++) {
        if (pl[si]) buildEngraveGroup(layer, [pl[si]], ta[si], fontName);
      }
      return;
    }
    /* linesOverride: positional front lines from a back-aware shape — keeps
       empty middle lines (line 3 stays line 3) instead of collapsing them */
    var lines = linesOverride || engravingLines();
    var maxLines = ta.lines || 1; if (lines.length > maxLines) lines = lines.slice(0, maxLines);
    if (!lines.join('').trim()) { layer.textContent = ''; return; }   // nothing to draw
    layer.textContent = '';
    ensureEngraveFonts(lines.join(''), fontName, function () { drawEngrave(svg, ta, linesOverride); });
    buildEngraveGroup(layer, lines, ta, fontName);
  }
  /* ---- back-side stage (shape.backTextArea, e.g. SPIN_NECKLACE's "Line 1
     (Back)"): the photo stage is duplicated beside the front one and the back
     engraving drawn on it, only while the back line has content. The front
     lines are the .symbol-input rows in DOM order; the back line is the
     symbol-dropdown component (its characters mirror into .active-character
     and overlay span n+1). backTextArea is a normal photo-space text box. ---- */
  function wfFrontLines(n) {
    var sins = document.querySelectorAll('.product-single__form .symbol-input');
    var spans = document.querySelectorAll('.pdp-overlay-container .preview-text');
    var out = [];
    for (var i = 0; i < n; i++) {
      var v = sins[i] ? (sins[i].value || '').trim() : '';
      if (!v && !sins.length && spans[i]) v = (spans[i].textContent || '').trim();
      out.push(v);
    }
    return out;
  }
  function wfBackText(n) {
    var comp = document.querySelector('.symbol-dropdown-parent .active-character');
    var v = comp ? (comp.textContent || '').trim() : '';
    if (!v) {
      var spans = document.querySelectorAll('.pdp-overlay-container .preview-text');
      if (spans[n]) v = (spans[n].textContent || '').trim();
    }
    return v;
  }
  /* if the chosen web font (or the Icons symbol font, when a symbol is in the
     text) isn't loaded yet the measurement would use a fallback (wrong
     metrics) — load it once, then redraw with the correct font. */
  function ensureEngraveFonts(allText, fontName, redraw) {
    try {
      var need = [];
      if (fontName && fontName !== 'Georgia') need.push(fontName);
      if (hasSym(allText)) need.push('Icons');
      need.forEach(function (fam) {
        if (document.fonts && document.fonts.check && !document.fonts.check('24px "' + fam + '"') && !WF_FONTS_TRIED[fam]) {
          WF_FONTS_TRIED[fam] = 1;
          document.fonts.load('24px "' + fam + '"').then(function () {
            redraw();                                   // redraw with correct per-font metrics
            try { WFZoom.sync(); } catch (e) {}         // refresh the open zoom modal too
          }, function () {});
        }
      });
    } catch (e) {}
  }
  /* curved engraving (ta.curve = arc bulge in viewBox units, + = dome up,
     - = valley down): the text follows a circular arc whose chord is the box
     width and whose sagitta is |curve|, centered vertically on the box (chord
     at y+curve/2, apex at y-curve/2). Font size fills the box height, shrunk
     if the text outruns the arc length. Rendered via <textPath> at final size
     (no group scaling), so the arc geometry stays true. */
  var WF_CURVE_SEQ = 0;
  function buildEngraveGroupCurve(layer, lines, ta, fontName) {
    var NS = 'http://www.w3.org/2000/svg';
    var REF = 100;
    var line = lines.filter(Boolean).join(' ');            // curved spots are single-line
    if (!line) return;
    var s = ta.curve, c = Math.max(ta.w, 4), as = Math.abs(s);
    var r = as / 2 + (c * c) / (8 * as);
    var arcLen = 2 * r * Math.asin(Math.min(1, c / (2 * r)));
    var bb = inkBounds([line], fontName, REF, REF * 1.05);
    var adv = (bb && bb.width) || line.length * REF * 0.6;
    var ih = (bb && bb.height) || REF * 0.72;
    var f = Math.min(ta.h / ih, arcLen / adv) * REF * (ta.fontScale || 1);
    var pid = 'wf-curve-' + (WF_CURVE_SEQ++);
    var y0 = ta.y + s / 2, sweep = s > 0 ? 1 : 0;
    var path = document.createElementNS(NS, 'path');
    path.setAttribute('id', pid); path.setAttribute('fill', 'none');
    path.setAttribute('d', 'M ' + (ta.x - c / 2) + ' ' + y0 + ' A ' + r.toFixed(2) + ' ' + r.toFixed(2) + ' 0 0 ' + sweep + ' ' + (ta.x + c / 2) + ' ' + y0);
    var text = document.createElementNS(NS, 'text');
    text.setAttribute('font-size', f.toFixed(2));
    text.setAttribute('font-family', fontName + ', Icons, Georgia, serif');
    text.setAttribute('fill', ta.color || '#f8f5ef');
    var tp = document.createElementNS(NS, 'textPath');
    tp.setAttribute('href', '#' + pid);
    tp.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#' + pid);   // Safari
    tp.setAttribute('startOffset', '50%');
    tp.setAttribute('text-anchor', 'middle');
    var runs = symRuns(line);
    if (runs.length === 1 && !runs[0].sym) {
      tp.textContent = line;
    } else {
      // symbol runs shrink to cap height and centre on the letters' ink, as in the flat path
      var plainC = runs.filter(function (r) { return !r.sym; }).map(function (r) { return r.s; }).join('');
      var pendC = 0;
      runs.forEach(function (rn) {
        var rs = document.createElementNS(NS, 'tspan');
        if (rn.sym) {
          var szC = f * symRunScale(fontName, f, rn.s);
          rs.setAttribute('font-size', szC.toFixed(1));
          var shC = symRunShift(fontName, f, plainC, rn.s, szC);
          if (shC) { rs.setAttribute('dy', (shC + pendC).toFixed(1)); pendC = -shC; }
          else if (pendC) { rs.setAttribute('dy', pendC.toFixed(1)); pendC = 0; }
        } else if (pendC) {
          rs.setAttribute('dy', pendC.toFixed(1)); pendC = 0;
        }
        rs.textContent = rn.s;
        tp.appendChild(rs);
      });
    }
    text.appendChild(tp);
    var g = document.createElementNS(NS, 'g');
    g.appendChild(path); g.appendChild(text);
    if (ta.rotate) g.setAttribute('transform', 'rotate(' + ta.rotate + ' ' + ta.x + ' ' + ta.y + ')');
    layer.appendChild(g);
  }
  /* build ONE positioned engraving text group for `lines` inside `ta` and
     append it to `layer` (never clears the layer — per-line spot groups
     coexist) */
  function buildEngraveGroup(layer, lines, ta, fontName) {
    if (ta.curve && Math.abs(ta.curve) >= 0.5) { buildEngraveGroupCurve(layer, lines, ta, fontName); return; }
    var NS = 'http://www.w3.org/2000/svg';
    var REF = 100, lh = REF * 1.05;

    // render at the reference size: each line centered on x=0, stacked by line-height
    var inner = document.createElementNS(NS, 'g');
    var text = document.createElementNS(NS, 'text');
    text.setAttribute('font-size', REF);
    text.setAttribute('font-family', fontName + ', Icons, Georgia, serif');   // Icons = the symbol glyphs, same stack as inkBounds
    // warm white on EVERY metal (matches the 3-style locket engraving fill
    // and real engraving photos). Legibility on silver comes from the PHOTOS:
    // the specular glare band is toned down to matte in the preview images
    // (merchant direction 2026-07-03; the earlier adaptive grey-frost + edge
    // copy for glare backdrops lives in git history if photos regress). An
    // explicitly authored non-default ta.color always wins.
    text.setAttribute('fill', ta.color || '#f8f5ef');
    text.setAttribute('text-anchor', 'middle');
    lines.forEach(function (l, i) {
      var ts = document.createElementNS(NS, 'tspan');
      ts.setAttribute('x', 0); ts.setAttribute('y', i * lh);
      var runs = symRuns(l);
      if (runs.length === 1 && !runs[0].sym) {
        ts.textContent = l;
      } else {
        // symbol runs shrink to cap height (symRunScale) and centre on the
        // letters' ink (symRunShift); dy is cumulative in SVG, so each shift
        // is undone on the next plain run
        var plain = runs.filter(function (r) { return !r.sym; }).map(function (r) { return r.s; }).join('');
        var pend = 0;
        runs.forEach(function (r) {
          var rs = document.createElementNS(NS, 'tspan');
          if (r.sym) {
            var size = REF * symRunScale(fontName, REF, r.s);
            rs.setAttribute('font-size', size.toFixed(1));
            var sh = symRunShift(fontName, REF, plain, r.s, size);
            if (sh) { rs.setAttribute('dy', (sh + pend).toFixed(1)); pend = -sh; }
            else if (pend) { rs.setAttribute('dy', pend.toFixed(1)); pend = 0; }
          } else if (pend) {
            rs.setAttribute('dy', pend.toFixed(1)); pend = 0;
          }
          rs.textContent = r.s;
          ts.appendChild(rs);
        });
      }
      text.appendChild(ts);
    });
    // NOTE: never put an SVG filter on this text — iOS Safari rasterizes a
    // filtered <text> through its layout box and flat-clips any ink painting
    // past it (angel halo, tall script ascenders) no matter the declared
    // filter region. If an engraved-edge effect returns, use a layered dark
    // text copy (see git history), not a filter.
    inner.appendChild(text);
    layer.appendChild(inner);

    // measure the real INK bounds (per-font) and uniformly scale to fill the box.
    // Ink bounds (canvas TextMetrics) are the font's true visible extent, so every
    // face — Magnum, Holland Land, scripts — sizes and centres consistently. Fall
    // back to SVG getBBox() (the em-box) only where the canvas metrics are missing.
    var bb = inkBounds(lines, fontName, REF, lh);
    if (!bb) { try { bb = text.getBBox(); } catch (e) { bb = null; } }
    if (!bb || !bb.width || !bb.height) {
      inner.setAttribute('transform', 'translate(' + ta.x + ' ' + ta.y + ')');   // un-measurable: at least position it
      return;
    }
    var s = Math.min(ta.w / bb.width, ta.h / bb.height) * (ta.fontScale || 1);
    var cx = bb.x + bb.width / 2, cy = bb.y + bb.height / 2;
    inner.setAttribute('transform',
      'translate(' + ta.x + ' ' + ta.y + ') scale(' + s.toFixed(4) + ') translate(' + (-cx).toFixed(2) + ' ' + (-cy).toFixed(2) + ')');

    if (ta.rotate) {   // rotate the placed text about the box centre
      var outer = document.createElementNS(NS, 'g');
      outer.setAttribute('transform', 'rotate(' + ta.rotate + ' ' + ta.x + ' ' + ta.y + ')');
      layer.removeChild(inner);                        // re-wrap in place; sibling spot groups stay
      outer.appendChild(inner);
      layer.appendChild(outer);
    }
  }

  /* a short text line for the modal (what's being engraved) — hidden when empty */
  function previewMetaLine() {
    var l = engravingLines();
    return l.length ? l.join('   ·   ') : '';
  }

  /* ---------------------------------------------------------------
     Shared live-preview ZOOM MODAL (mock .lc-modal). Each preview builder adds a
     zoom button to its card; tapping it opens this single modal showing the SAME
     live preview, large:
       - svg / image variants  -> clone the live <svg> into the modal stage and
         re-clone on every edit (WFZoom.sync()), so it stays live.
       - photo-overlay fallback -> move the live stage into the modal and back out
         on close (the overlay can't be cloned live), re-fitting via a resize event.
     --------------------------------------------------------------- */
  var WFZoom = (function () {
    var modal = null, slot = null, mmeta = null;
    var liveGetSvg = null, onCloseCb = null, lastFocus = null, isOpen = false;

    function ensure() {
      if (modal) return;
      modal = document.createElement('div');
      modal.className = 'wf-lc-modal'; modal.hidden = true;
      modal.innerHTML =
        '<div class="wf-lc-modal-bg" data-lc-close></div>' +
        '<div class="wf-lc-modal-card" role="dialog" aria-modal="true" aria-label="Live engraving preview">' +
          '<button class="wf-lc-close" type="button" aria-label="Close preview" data-lc-close>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
          '</button>' +
          '<div class="wf-lc-mcap">Live preview</div>' +
          '<div class="wf-lc-mstage"></div>' +
          '<div class="wf-lc-mmeta" hidden></div>' +
        '</div>';
      document.body.appendChild(modal);
      slot = modal.querySelector('.wf-lc-mstage');
      mmeta = modal.querySelector('.wf-lc-mmeta');
      modal.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest('[data-lc-close]')) close();
      });
      document.addEventListener('keydown', function (e) {
        if (!isOpen) return;
        if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) { close(); return; }
        if (e.key === 'Tab' || e.keyCode === 9) {                       // trap focus inside the dialog card
          var card = modal.querySelector('.wf-lc-modal-card'); if (!card) return;
          var f = [].filter.call(
            card.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
            function (el) { return !el.disabled && el.offsetParent !== null; });
          if (!f.length) { e.preventDefault(); return; }
          var first = f[0], last = f[f.length - 1], a = document.activeElement;
          if (e.shiftKey && (a === first || !card.contains(a))) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && (a === last || !card.contains(a))) { e.preventDefault(); first.focus(); }
        }
      });
    }

    function setMeta(t) {
      if (!mmeta) return;
      if (t) { mmeta.textContent = t; mmeta.hidden = false; }
      else { mmeta.textContent = ''; mmeta.hidden = true; }
    }

    /* make a cloned svg self-contained: rewrite its element ids (e.g. #wf-metal)
       and url(#id) references so duplicate ids don't resolve to the card's svg */
    function uniquifyIds(node, suffix) {
      var ATTRS = ['fill', 'stroke', 'clip-path', 'filter', 'mask'];
      var ided = node.querySelectorAll('[id]');
      for (var i = 0; i < ided.length; i++) {
        var oldId = ided[i].id, newId = oldId + suffix;
        ided[i].id = newId;
        var refs = node.querySelectorAll('*');
        for (var j = 0; j < refs.length; j++) {
          for (var k = 0; k < ATTRS.length; k++) {
            var v = refs[j].getAttribute(ATTRS[k]);
            if (v && v.indexOf('url(#' + oldId + ')') > -1) {
              refs[j].setAttribute(ATTRS[k], v.replace('url(#' + oldId + ')', 'url(#' + newId + ')'));
            }
          }
          // direct references too (textPath href to its curve path)
          if (refs[j].getAttribute('href') === '#' + oldId) refs[j].setAttribute('href', '#' + newId);
          if (refs[j].getAttribute('xlink:href') === '#' + oldId) {
            refs[j].setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#' + newId);
          }
        }
      }
    }

    function sync() {
      if (!isOpen || !liveGetSvg || !slot) return;
      var src = liveGetSvg(); if (!src) return;
      var clone = src.cloneNode(true);
      uniquifyIds(clone, '-lcm');
      // carry the card's sampled image background onto the modal stage
      var card = src.closest ? src.closest('.wf-livecard') : null;
      var bgv = card ? card.style.getPropertyValue('--wf-img-stage-bg') : '';
      if (bgv) slot.style.setProperty('--wf-img-stage-bg', bgv);
      else slot.style.removeProperty('--wf-img-stage-bg');
      slot.innerHTML = '';
      slot.appendChild(clone);
    }

    function open(cfg) {
      ensure();
      if (isOpen) return;                              // never re-open over an already-open modal
      lastFocus = document.activeElement;
      onCloseCb = (cfg && cfg.onClose) || null;
      liveGetSvg = (cfg && cfg.getSvg) || null;
      slot.className = 'wf-lc-mstage' + (cfg && cfg.svgMode === 'svg' ? ' wf-lc-mstage--svg' : '');
      slot.innerHTML = '';
      setMeta(cfg && cfg.meta);
      // Make the modal visible and flag it open BEFORE mount/sync, so the slot has
      // real layout (photo fit() reads clientWidth) and sync()'s isOpen guard passes.
      modal.hidden = false; isOpen = true;
      document.documentElement.classList.add('wf-lc-lock');
      if (cfg && cfg.mount) { try { cfg.mount(slot); } catch (e) {} }   // photo: move live stage in + fit
      sync();                                                            // svg/img: clone live svg into slot
      var c = modal.querySelector('.wf-lc-close'); if (c) { try { c.focus(); } catch (e) {} }
    }

    function close() {
      if (!isOpen) return;
      isOpen = false; modal.hidden = true;
      document.documentElement.classList.remove('wf-lc-lock');
      var cb = onCloseCb; onCloseCb = null; liveGetSvg = null;
      if (cb) { try { cb(slot); } catch (e) {} }
      if (slot) slot.innerHTML = '';
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    }

    function addBtn(card, onClick) {
      // zoom button removed from the card (preview is now full-width); the
      // modal machinery stays dormant behind it in case it comes back
    }

    return {
      sync: sync,
      /* clone-and-resync zoom for the svg / image preview variants */
      attachSvg: function (card, getSvg, svgMode, getMeta) {
        addBtn(card, function () {
          open({ getSvg: getSvg, svgMode: svgMode, meta: getMeta && getMeta() });
        });
      },
      /* move-the-live-stage zoom for the photo-overlay fallback */
      attachMove: function (card, stage, getMeta) {
        addBtn(card, function () {
          var home = { parent: stage.parentNode, next: stage.nextSibling };
          var frozen = card.offsetHeight;
          open({
            meta: getMeta && getMeta(),
            mount: function (sl) {
              card.style.minHeight = frozen + 'px';   // keep the card from collapsing behind the blur
              sl.appendChild(stage);
              try { window.dispatchEvent(new Event('resize')); } catch (e) {}   // re-fit the overlay to the big stage
            },
            onClose: function () {
              if (home.next && home.next.parentNode === home.parent) home.parent.insertBefore(stage, home.next);
              else if (home.parent) home.parent.appendChild(stage);
              card.style.minHeight = '';
              try { window.dispatchEvent(new Event('resize')); } catch (e) {}
            }
          });
        });
      }
    };
  })();

  /* Content-aware vertical crop for the image-preview stage. The merchant's
     preview images are squares whose product may be a short band (bar ring) or
     nearly full-bleed (oval locket ring) — a fixed crop cuts the latter off.
     Measure the image's content rows on a small canvas (background = corner
     color), union with the engraving box so text is never cropped, pad, clamp
     to the drawn image band and to [0.15, 1] of the square, and expose the
     window as CSS vars on the card (--wf-img-crop-top / --wf-img-crop-h;
     defaults mean "no crop"). */
  /* crop / tone sampling need one axis-aligned rectangle — the bounding box of
     all spots (textAreas array) or of the single textArea, accounting for
     rotate: a -90° bar (vertical bar charm) extends vertically by its WIDTH,
     and the crop must reserve that, not the unrotated height */
  function taUnion(ta) {
    var list = (ta && typeof ta.length === 'number') ? ta : (ta ? [ta] : []);
    var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (var i = 0; i < list.length; i++) {
      var t = list[i], r = (t.rotate || 0) * Math.PI / 180;
      var eh = t.h + Math.abs(t.curve || 0);   // a curved arc bulges past the box height
      var hw = (Math.abs(t.w * Math.cos(r)) + Math.abs(eh * Math.sin(r))) / 2;
      var hh = (Math.abs(t.w * Math.sin(r)) + Math.abs(eh * Math.cos(r))) / 2;
      x0 = Math.min(x0, t.x - hw); y0 = Math.min(y0, t.y - hh);
      x1 = Math.max(x1, t.x + hw); y1 = Math.max(y1, t.y + hh);
    }
    if (!isFinite(x0)) return null;
    return { x: (x0 + x1) / 2, y: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
  }
  var WF_CROP_CACHE = {};
  function applyStageCrop(card, url, ta, onTone) {
    if (!url) return;
    function set(c) {
      card._wfCrop = c;
      card.style.setProperty('--wf-img-crop-top', c.top);
      card.style.setProperty('--wf-img-crop-h', c.h);
      if (c.bg) card.style.setProperty('--wf-img-stage-bg', c.bg);
      else card.style.removeProperty('--wf-img-stage-bg');
      applyStageZoom(card);
      // hand the measured backdrop color to the engraving renderer;
      // redraw once when it first arrives (image decode is async)
      var sv = card.querySelector('.wf-preview-svg');
      if (sv && (sv._wfTaLum !== c.lum || sv._wfTaChroma !== c.chroma)) {
        sv._wfTaLum = (c.lum == null ? null : c.lum);
        sv._wfTaChroma = (c.chroma == null ? null : c.chroma);
        if (typeof onTone === 'function') onTone();
      }
    }
    var cached = WF_CROP_CACHE[url];
    card.dataset.wfCropUrl = url;
    if (cached) { set(cached); return; }
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = function () {
      var crop = { top: 0, h: 1 };
      try {
        var S = 80, cv = document.createElement('canvas');
        cv.width = S; cv.height = S;
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(im, 0, 0, S, S);
        var d = cx.getImageData(0, 0, S, S).data;
        var corners = [0, (S - 1) * 4, (S - 1) * S * 4, (S * S - 1) * 4];
        var bg = [0, 1, 2].map(function (c) {
          return (d[corners[0] + c] + d[corners[1] + c] + d[corners[2] + c] + d[corners[3] + c]) / 4;
        });
        // stage backdrop matches the image's own background, so any letterboxing
        // around a non-square image blends in instead of showing white bars
        var bgCss = 'rgb(' + Math.round(bg[0]) + ',' + Math.round(bg[1]) + ',' + Math.round(bg[2]) + ')';
        crop.bg = bgCss;
        var TH = 28, top = -1, bot = -1;
        for (var y = 0; y < S; y++) {
          for (var x = 0; x < S; x++) {
            var i = (y * S + x) * 4;
            if (Math.abs(d[i] - bg[0]) > TH || Math.abs(d[i + 1] - bg[1]) > TH || Math.abs(d[i + 2] - bg[2]) > TH) {
              if (top < 0) top = y;
              bot = y;
              break;
            }
          }
        }
        if (top > -1) {
          // to viewBox units: the <image> is meet-fitted inside the 200x200 box
          var ar = im.naturalWidth / im.naturalHeight;
          var drawnH = ar >= 1 ? 200 / ar : 200;
          var offY = (200 - drawnH) / 2;
          var y0 = offY + (top / S) * drawnH;
          var y1 = offY + ((bot + 1) / S) * drawnH;
          if (ta) {                                    // never crop the engraving box
            y0 = Math.min(y0, ta.y - ta.h / 2 - 4);
            y1 = Math.max(y1, ta.y + ta.h / 2 + 4);
          }
          var PAD = 10;
          y0 = Math.max(0, y0 - PAD); y1 = Math.min(200, y1 + PAD);
          // never show past the drawn image itself: a misplaced engraving box
          // (or padding) must not reserve empty letterbox rows in the stage
          y0 = Math.max(y0, offY); y1 = Math.min(y1, offY + drawnH);
          var h = Math.min(1, Math.max(0.15, (y1 - y0) / 200));
          var center = (y0 + y1) / 400;
          // mean color of the metal under the engraving box — drawEngrave flips
          // the text tone on BRIGHT-NEUTRAL backdrops (polished silver / glare,
          // where real frosting reads dark). Chroma keeps gold (bright but
          // yellow) and black on the default warm white.
          var lum = null, chroma = null;
          if (ta) {
            var drawnW = ar >= 1 ? 200 : 200 * ar, offX = (200 - drawnW) / 2;
            var lx0 = Math.max(0, Math.round((ta.x - ta.w / 2 - offX) / drawnW * S));
            var lx1 = Math.min(S - 1, Math.round((ta.x + ta.w / 2 - offX) / drawnW * S));
            var ly0 = Math.max(0, Math.round((ta.y - ta.h / 2 - offY) / drawnH * S));
            var ly1 = Math.min(S - 1, Math.round((ta.y + ta.h / 2 - offY) / drawnH * S));
            var sr = 0, sg = 0, sb = 0, n = 0;
            for (var yy = ly0; yy <= ly1; yy++) {
              for (var xx = lx0; xx <= lx1; xx++) {
                var ll = (yy * S + xx) * 4;
                sr += d[ll]; sg += d[ll + 1]; sb += d[ll + 2]; n++;
              }
            }
            if (n) {
              sr /= n; sg /= n; sb /= n;
              lum = 0.299 * sr + 0.587 * sg + 0.114 * sb;
              chroma = Math.max(sr, sg, sb) - Math.min(sr, sg, sb);
            }
          }
          crop = { top: Math.max(0, Math.min(1 - h, center - h / 2)), h: h, bg: bgCss, lum: lum, chroma: chroma };
        }
      } catch (e) { /* tainted canvas etc. -> full square */ }
      WF_CROP_CACHE[url] = crop;
      if (card.dataset.wfCropUrl === url) set(crop);   // still the current image
    };
    im.src = url;
  }

  /* Wide products have a short content band and render small inside the fixed
     square stage — scale the whole square up (--wf-img-zoom) until the band
     fills the card's inner width, capped so the band never grows taller than
     the base square. Everything scales together, so image + engraving stay
     registered. */
  function applyStageZoom(card) {
    var c = card._wfCrop || { top: 0, h: 1 };
    var S = Math.min(window.innerWidth * 0.54, 170);      // base square = css min(54vw, 170px)
    var maxW = Math.max(S, (card.clientWidth || S) - 36); // inner width (18px side padding)
    // twin (front+back) stages share the row: each may only grow to half of it
    if (card.classList.contains('wf-livecard--twin')) maxW = Math.max(S, (maxW - 10) / 2);
    var z = Math.max(1, Math.min(1 / c.h, maxW / S));
    card.style.setProperty('--wf-img-zoom', Math.round(z * 100) / 100);
  }

  /* ---- 2a-i. Engraving Preview Image: the merchant's per-variant
     custom.engraving_preview_image as the backdrop, with the live engraving drawn
     on top (text-area from the authored shape geometry). Preferred over the drawn
     SVG when an image exists. ---- */
  function buildMetafieldPreview() {
    var imgs = window.WF_PREVIEW_IMAGES; if (!imgs) return false;
    function firstImg() { for (var k in imgs) { if (imgs[k]) return imgs[k]; } return null; }
    var url = (function () { var v = currentVariantId(); return (v && imgs[v]) || firstImg(); })();
    if (!url) return false;

    var SH = window.WF_PREVIEW_SHAPES || {};
    var ot = (document.getElementById('ornament-type') || {}).value || '';
    // textAreas (per-line spots, e.g. CIRCLE_QUADRANT_NECKLACE) wins over the single textArea
    var ta = (SH[ot] && (SH[ot].textAreas || SH[ot].textArea)) || { x: 100, y: 118, w: 130, h: 46, rotate: 0, lines: 1, maxChars: 11, color: '#f8f5ef' };
    var backTa = (SH[ot] && SH[ot].backTextArea) || null;

    var card = document.createElement('div'); card.className = 'wf-livecard wf-livecard--img';
    var stage = document.createElement('div'); stage.className = 'wf-livecard-stage wf-img-stage';
    var cap = document.createElement('div'); cap.className = 'wf-livecard-cap'; cap.textContent = 'Live preview';
    function makeSvg() {
      var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      s.setAttribute('viewBox', '0 0 200 200'); s.setAttribute('class', 'wf-preview-svg wf-preview-img');
      s.innerHTML = '<image class="wf-bg-img" x="0" y="0" width="200" height="200" preserveAspectRatio="xMidYMid meet"></image><g class="wf-engrave-layer"></g>';
      return s;
    }
    var svg = makeSvg();
    function setImg(u) {
      card.querySelectorAll('.wf-bg-img').forEach(function (im) {
        im.setAttribute('href', u); im.setAttributeNS('http://www.w3.org/1999/xlink', 'href', u);
      });
    }
    stage.appendChild(svg);
    card.appendChild(cap);
    var backSvg = null;
    if (backTa) {
      /* twin stages: the back side is the same photo stage duplicated beside
         the front, its engraving drawn from the back line. Both stages share
         the card's crop/zoom CSS vars, so they stay registered. The back wrap
         is CSS-hidden until the back line has content (.wf-livecard--twin). */
      var row = document.createElement('div'); row.className = 'wf-stage-row';
      var fw = document.createElement('div'); fw.className = 'wf-stage-wrap';
      var flb = document.createElement('div'); flb.className = 'wf-stage-label'; flb.textContent = 'Front';
      fw.appendChild(stage); fw.appendChild(flb);
      var bw = document.createElement('div'); bw.className = 'wf-stage-wrap wf-back-wrap';
      var bStage = document.createElement('div'); bStage.className = 'wf-livecard-stage wf-img-stage';
      backSvg = makeSvg();
      bStage.appendChild(backSvg);
      var blb = document.createElement('div'); blb.className = 'wf-stage-label'; blb.textContent = 'Back';
      bw.appendChild(bStage); bw.appendChild(blb);
      row.appendChild(fw); row.appendChild(bw);
      card.appendChild(row);
    } else {
      card.appendChild(stage);
    }
    setImg(url);

    var insertBefore = atcAnchor() || photoUploadEl();
    if (!insertBefore || !insertBefore.parentNode) return false;
    insertBefore.parentNode.insertBefore(card, insertBefore);

    var vp = document.getElementById('viewPreview');
    if (vp) { vp.classList.add('wf-gone'); var w = vp.closest('.upload'); if (w && !w.querySelector('#upload-file')) w.classList.add('wf-gone'); }

    WFZoom.attachSvg(card, function () { return svg; }, 'img', previewMetaLine);

    var curUrl = url;
    function cropBox() {
      return backTa ? taUnion([].concat(ta).concat([backTa])) : taUnion(ta);
    }
    function drawAll() {
      if (backTa) {
        var n = (typeof ta.length === 'number') ? ta.length : (ta.lines || 1);
        var front = wfFrontLines(n);
        while (front.length && !front[front.length - 1]) front.pop();
        drawEngrave(svg, ta, front);
        var backText = wfBackText(n);
        card.classList.toggle('wf-livecard--twin', !!backText);
        if (backSvg) {
          var layer = backSvg.querySelector('.wf-engrave-layer');
          layer.textContent = '';
          if (backText) {
            var fontName = engravingFont().replace(/"/g, '');
            ensureEngraveFonts(backText, fontName, function () { drawAll(); WFZoom.sync(); });
            buildEngraveGroup(layer, [backText], backTa, fontName);
          }
        }
        applyStageZoom(card);
      } else {
        drawEngrave(svg, ta);
      }
    }
    function render() {
      var v = currentVariantId(); var u = v && imgs[v]; if (u) { setImg(u); curUrl = u; }   // swap image on variant change
      applyStageCrop(card, curUrl, cropBox(), function () { drawAll(); WFZoom.sync(); });
      drawAll();
      WFZoom.sync();                                                        // keep the open modal in sync
    }
    render();
    var deb = null;
    function onChange() { if (deb) clearTimeout(deb); deb = setTimeout(render, 60); }
    document.addEventListener('input', onChange, true);
    document.addEventListener('change', onChange, true);
    document.addEventListener('click', function (e) { if (e.target && e.target.closest && e.target.closest('.font-button, .variant-input, .color-swatch')) onChange(); }, true);
    var oc = document.querySelector('.pdp-overlay-container');
    if (oc) { try { new MutationObserver(onChange).observe(oc, { subtree: true, childList: true, characterData: true }); } catch (e) {} }
    var rz = null;
    window.addEventListener('resize', function () { if (rz) clearTimeout(rz); rz = setTimeout(function () { applyStageZoom(card); }, 150); });
    return true;
  }

  function buildSvgPreview() {
    var SH = window.WF_PREVIEW_SHAPES; if (!SH) return false;
    var ot = (document.getElementById('ornament-type') || {}).value || '';
    var shape = SH[ot]; if (!shape || !shape.svgInner) return false;

    var card = document.createElement('div'); card.className = 'wf-livecard wf-livecard--svg';
    var stage = document.createElement('div'); stage.className = 'wf-livecard-stage wf-svg-stage';
    var cap = document.createElement('div'); cap.className = 'wf-livecard-cap'; cap.textContent = 'Live preview';
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', shape.viewBox || '0 0 200 200');
    svg.setAttribute('class', 'wf-preview-svg');
    svg.innerHTML =
      '<defs><linearGradient id="wf-metal" x1="0" y1="0" x2="0" y2="1">' +
      '<stop class="wf-m0" offset="0"/><stop class="wf-m1" offset="0.5"/><stop class="wf-m2" offset="1"/></linearGradient></defs>' +
      shape.svgInner + '<g class="wf-engrave-layer"></g>';
    stage.appendChild(svg);
    card.appendChild(cap); card.appendChild(stage);

    var insertBefore = atcAnchor() || photoUploadEl();
    if (!insertBefore || !insertBefore.parentNode) return false;
    insertBefore.parentNode.insertBefore(card, insertBefore);

    var vp = document.getElementById('viewPreview');
    if (vp) { vp.classList.add('wf-gone'); var w = vp.closest('.upload'); if (w && !w.querySelector('#upload-file')) w.classList.add('wf-gone'); }

    WFZoom.attachSvg(card, function () { return svg; }, 'svg', previewMetaLine);

    var ta = shape.textAreas || shape.textArea || { x: 100, y: 100, w: 100, h: 40, rotate: 0, lines: 1, maxChars: 9 };
    function render() {
      var m = WF_METALS[selectedMetalKey()] || WF_METALS.silver;
      var g = svg.querySelector('#wf-metal');
      g.querySelector('.wf-m0').setAttribute('stop-color', m[0]);
      g.querySelector('.wf-m1').setAttribute('stop-color', m[1]);
      g.querySelector('.wf-m2').setAttribute('stop-color', m[2]);
      drawEngrave(svg, ta);
      WFZoom.sync();                                                        // keep the open modal in sync
    }
    render();
    var deb = null;
    function onChange() { if (deb) clearTimeout(deb); deb = setTimeout(render, 60); }
    document.addEventListener('input', onChange, true);
    document.addEventListener('change', onChange, true);
    document.addEventListener('click', function (e) { if (e.target && e.target.closest && e.target.closest('.font-button, .variant-input, .color-swatch')) onChange(); }, true);
    var oc = document.querySelector('.pdp-overlay-container');
    if (oc) { try { new MutationObserver(onChange).observe(oc, { subtree: true, childList: true, characterData: true }); } catch (e) {} }
    return true;
  }

  function buildPhotoPreview() {
    if (VARIANT !== 'B') return;
    if (document.querySelector('.wf-livecard')) return;
    var oc = document.querySelector('.pdp-overlay-container');
    if (!oc || !oc.querySelector('canvas, .preview-text')) return;  // nothing to preview yet

    // natural square size while still positioned over the gallery
    var natW = Math.round(oc.getBoundingClientRect().width) || oc.offsetWidth || 440;
    if (natW < 120) natW = 440;

    var card = document.createElement('div');
    card.className = 'wf-livecard';
    var stage = document.createElement('div'); stage.className = 'wf-livecard-stage';
    var cap = document.createElement('div'); cap.className = 'wf-livecard-cap'; cap.textContent = 'Live preview';
    card.appendChild(cap); card.appendChild(stage);

    // the PRODUCT PHOTO is the stage backdrop (the product's shape); the relocated
    // overlay contributes only the live engraving text/canvas on top.
    var ps = productPhotoSrc();
    if (ps) stage.style.backgroundImage = 'url("' + ps + '")';
    var oimg = oc.querySelector('img'); if (oimg) oimg.style.display = 'none';

    stage.appendChild(oc);
    oc.style.width = natW + 'px';
    oc.style.height = natW + 'px';
    function fit() {
      var sw = stage.clientWidth || 300;
      oc.style.transform = 'scale(' + (sw / natW) + ')';
      stage.style.height = Math.round(sw) + 'px';   // square stage matches square overlay
    }

    // place just above the ATC (below the photo uploader)
    var insertBefore = atcAnchor() || photoUploadEl();
    if (!insertBefore || !insertBefore.parentNode) return;
    insertBefore.parentNode.insertBefore(card, insertBefore);
    fit();
    window.addEventListener('resize', fit);

    WFZoom.attachMove(card, stage, previewMetaLine);

    // the inline card replaces the now-redundant "View Preview" button
    var vp = document.getElementById('viewPreview');
    if (vp) {
      vp.classList.add('wf-gone');
      var w = vp.closest('.upload');
      if (w && !w.querySelector('#upload-file')) w.classList.add('wf-gone');
    }
  }

  function buildLivePreview() {
    if (VARIANT !== 'B') return;
    if (document.querySelector('.wf-livecard')) return;
    if (buildMetafieldPreview()) return;  // merchant "Preview Image" metafield (preferred)
    if (buildSvgPreview()) return;         // authored Claude-Design SVG shape
    buildPhotoPreview();                   // fallback: gallery photo + theme overlay
  }

  /* ---- 4. Match the design's engraving input: add a "Up to N letters" placeholder
     and strip the "(N characters)" suffix from the field labels (the count already
     sits on the right of the input). ---- */
  function tidyEngravingLabels() {
    if (VARIANT !== 'B') return;                            // control A must match production
    var root = document.querySelector('.product-single__form'); if (!root) return;
    root.querySelectorAll('.symbol-input, .custom-ornament-field.input input[type="text"], .custom-ring-field.input input[type="text"], .custom-bracelet-field.input input[type="text"]').forEach(function (inp) {
      if (inp.dataset.wfPh === '1') return;
      var max = parseInt(inp.getAttribute('maxlength'), 10);
      if (max && !(inp.getAttribute('placeholder') || '').trim()) inp.setAttribute('placeholder', max === 1 ? '1 character' : 'Up to ' + max + ' letters');
      inp.dataset.wfPh = '1';
    });
    // no once-only flag here: the theme rewrites these labels (re-adding the
    // count) on variant/config changes, so strip by content every pass — the
    // observer loop settles because nothing is written unless a count matches
    root.querySelectorAll('.symbol-input-container label, .symbol-input-container .symbol-dropdown-label, .symbol-input-label, .custom-ornament-field > label, .custom-ring-field > label, .custom-bracelet-field > label').forEach(function (l) {
      [].slice.call(l.querySelectorAll('*')).forEach(function (c) {
        if (!c.children.length && /^\(\s*\d+\s*characters?\s*\)$/i.test((c.textContent || '').trim())) c.remove();
      });
      for (var i = 0; i < l.childNodes.length; i++) {
        var n = l.childNodes[i];
        if (n.nodeType === 3 && /\(\s*\d+\s*characters?\s*\)/i.test(n.nodeValue)) {
          n.nodeValue = n.nodeValue.replace(/\(\s*\d+\s*characters?\s*\)/ig, '').replace(/\s{2,}/g, ' ');
        }
      }
    });
  }

  /* ---- 4b. Caret to the END when first clicking into a filled engraving
     input. The theme rebuilds these inputs' values (symbols live in
     data-char-N and the value gets reconstructed), which can leave the caret
     at position 0 on focus. Only the focusing interaction is corrected —
     clicks while already focused position the caret freely. ---- */
  function wireCaretToEnd() {
    if (VARIANT !== 'B') return;
    if (document._wfCaretWired) return;
    document._wfCaretWired = true;
    var SEL = '.symbol-input, .custom-ornament-field.input input[type="text"], .custom-ring-field.input input[type="text"], .custom-bracelet-field.input input[type="text"]';
    document.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!el || !el.matches || !el.matches(SEL)) return;
      function toEnd() {
        try {
          if (document.activeElement !== el) return;
          var n = (el.value || '').length;
          if (n) el.setSelectionRange(n, n);
        } catch (err) {}
      }
      setTimeout(toEnd, 0);
      // the pointerup completing the focusing click can re-place the caret —
      // correct once more right after it, then leave the user alone
      el.addEventListener('pointerup', function () { setTimeout(toEnd, 0); }, { once: true });
    });
  }

  /* ---- 5. Uploaded photo shows inside the upload button's icon tile (not the
     theme's .upload__preview-wrap card), clipped to the product's crop shape. ---- */
  function injectHeartClip() {
    if (document.getElementById('wf-heart-clip')) return;
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '0'); svg.setAttribute('height', '0'); svg.setAttribute('aria-hidden', 'true');
    svg.style.cssText = 'position:absolute;width:0;height:0;pointer-events:none';
    svg.innerHTML = '<defs><clipPath id="wf-heart-clip" clipPathUnits="objectBoundingBox"><path d="M0.5 1 C0.324 0.899 0 0.681 0 0.333 C0 0.116 0.135 0 0.284 0 C0.392 0 0.473 0.058 0.5 0.174 C0.527 0.058 0.608 0 0.716 0 C0.865 0 1 0.116 1 0.333 C1 0.681 0.676 0.899 0.5 1 Z"/></clipPath></defs>';
    document.body.appendChild(svg);
  }
  function photoShape() {
    var lt = ((document.getElementById('locket-type') || {}).value || '');
    if (/heart/i.test(lt)) return 'heart';
    if (/oval/i.test(lt)) return 'oval';
    if (/rectangle-vertical/i.test(lt)) return 'portrait';
    if (/rectangle|memorial-frame/i.test(lt)) return 'square';
    if (/cocktail/i.test(lt)) return 'circle';
    var ot = ((document.getElementById('ornament-type') || {}).value || '');
    if (/HEART/.test(ot)) return 'heart';
    if (/OVAL/.test(ot)) return 'oval';
    if (/SQUARE|RECTANGLE/.test(ot)) return 'square';
    return 'circle';
  }
  function wirePhotoThumb() {
    if (VARIANT !== 'B') return;
    var preview = document.getElementById('upload-preview');
    var btn = document.querySelector('.product-single__form .upload .btn--upload:not(#viewPreview)');
    if (!preview || !btn || btn.dataset.wfPhotoWired === '1') return;
    btn.dataset.wfPhotoWired = '1';
    injectHeartClip();
    btn.classList.add('wf-photo-' + photoShape());
    var label = document.getElementById('upload-btn-text');
    function realSrc() { var s = preview.getAttribute('src') || ''; return (!s || s.indexOf('data:image/svg') > -1) ? '' : s; }
    function apply() {
      var src = realSrc();
      if (src) {
        btn.style.setProperty('--wf-thumb', 'url("' + src + '")');
        btn.classList.add('wf-has-photo');
        if (label) label.innerHTML = 'Photo added<small>Tap to change</small>';
      } else {
        btn.classList.remove('wf-has-photo');
        btn.style.removeProperty('--wf-thumb');
        if (label && !label.querySelector('input')) label.textContent = 'Upload Photo';
      }
    }
    apply();
    preview.addEventListener('load', apply);            // wins over the theme's inline "Change Photo" handler
    if ('MutationObserver' in window) new MutationObserver(apply).observe(preview, { attributes: true, attributeFilter: ['src'] });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* create the character count for symbol fields that lack one (the theme only
     builds it when an "Add symbol" button is present — fields without symbols got none) */
  function ensureSymbolCounts() {
    if (VARIANT !== 'B') return;                            // control A must match production
    document.querySelectorAll('.product-single__form .symbol-input-container').forEach(function (c) {
      if (c.querySelector('.symbol-input-count')) return;
      var inner = c.querySelector('.symbol-input-inner'); if (!inner) return;
      var input = inner.querySelector('.symbol-input'); if (!input) return;
      var limit = parseInt(c.getAttribute('data-char-limit'), 10);
      if (!limit) limit = parseInt(input.getAttribute('maxlength'), 10);
      if (!limit) return;
      var span = document.createElement('span');
      span.className = 'symbol-input-count';
      var btn = inner.querySelector('.symbol-input-button');
      if (btn) inner.insertBefore(span, btn); else inner.appendChild(span);
      var upd = function () { span.textContent = (input.value || '').length + '/' + limit; };
      upd();
      input.addEventListener('input', upd);
      input.addEventListener('change', upd);
    });
  }

  /* Legacy customring pages (e.g. Engravable Heart Ring): read like the circle
     signet page on B — font picker above the engraving input, and the big
     symbols grid collapsed by default (zodiac already is). One-shot guards so
     reopening the accordion isn't fought, and A keeps the production layout. */
  function tidyLegacyCustomring() {
    if (VARIANT !== 'B') return;
    var blk = document.querySelector('.product-single__form .wf-customring-block');
    if (!blk) return;
    var chk = blk.querySelector('#symbols-check');
    if (chk && chk.dataset.wfCollapsed !== '1') { chk.dataset.wfCollapsed = '1'; chk.checked = false; }
    if (blk.dataset.wfFontFirst !== '1') {
      var input = blk.querySelector('.custom-ring-field.input');
      var label = blk.querySelector('label[for="font-container"]');
      var font = blk.querySelector('#font-container');
      if (input && label && font && font.children.length) {
        blk.insertBefore(label, input);
        blk.insertBefore(font, input);
        blk.dataset.wfFontFirst = '1';
      }
    }
  }

  function tick() { buildStickyAtc(); moveUploadToBottom(); buildLivePreview(); tidyEngravingLabels(); ensureSymbolCounts(); wireCaretToEnd(); wirePhotoThumb(); tidyLegacyCustomring(); }

  if (AB.active) trackAssignment();              // test concluded -> no stamping/GA4
  ready(function () {
    tick();
    // personalizer markup mounts late (minified theme JS) — retry as it appears
    [600, 1200, 2000, 3000].forEach(function (t) { setTimeout(tick, t); });
    // late mounts / re-renders — keep things tidy (guards prevent loops)
    var form = document.querySelector('.product-single__form');
    if (form) {
      try {
        var deb;
        new MutationObserver(function () {
          // strip label counts synchronously: MO callbacks are microtasks, so
          // this runs before the browser paints the theme's rewritten label —
          // no "(6 characters)" flash while typing. Writes only happen when a
          // count is present, so the observer loop settles.
          tidyEngravingLabels();
          clearTimeout(deb); deb = setTimeout(function () { wirePhotoThumb(); }, 80);
        }).observe(form, { childList: true, subtree: true, characterData: true });
      } catch (e) {}
    }
  });
})();
